import {isEmpty, some} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, FileInput, Loader, Dropdown, DropdownItem} from 'components';
import {SessionTextChatMessageOwner} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {sessionUtils} from 'utils';

import {AiSuggestionsPopup} from './AiSuggestionsPopup';
import {CompliancePanel} from './CompliancePanel';
import {TextChatMessageList} from './TextChatMessageList/TextChatMessageList';
import {SessionTextChatViewProps} from './types';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

export const SessionTextChatView: React.FC<SessionTextChatViewProps> = (props) => {
    const {
        message, messages, fileIsUploading, localParticipantId, downloadingFiles, aiSuggestionsError, participants,
        aiSuggestionsEnabled, typingRemoteParticipantIds, downloadFileDisabled, aiSuggestions, aiSuggestionsLoading,
        submitDisabled, sendInProgress, messagesEndRef, setSendFileInputRef, onChange, downloadFile, userParticipantIds,
        onSubmit, handleUploadFile, openFileFileInput, requestAiSuggestions, getTranslation, uploadFileHidden,
        complianceFlagged, onComplianceAccept, onComplianceIgnore, onComplianceDismiss, voiceInput
    } = props;
    const {t} = useTranslation();
    const [aiSuggestionsModalOpen, setAiSuggestionsModalOpen] = useState(false);

    const handleVoiceStart = useCallback(() => voiceInput?.onStart(), [voiceInput]);
    const handleVoiceStop = useCallback(() => voiceInput?.onStop(), [voiceInput]);

    const aiSuggestionsDisabled = useMemo(() => isEmpty(messages) || !some(messages,
        (msg) => sessionUtils.getTextChatMessageOwner(msg?.from.id, localParticipantId) === SessionTextChatMessageOwner.Foreign
    ), [messages, localParticipantId]);

    const handleOpenChatbotHelp = useCallback(() => {
        setAiSuggestionsModalOpen(true);
        requestAiSuggestions();
    }, [requestAiSuggestions]);

    const handleCloseChatbotHelp = useCallback(() => {
        onChange('');
        setAiSuggestionsModalOpen(false);
    }, [onChange]);

    const handleSubmitSuggestion = useCallback(() => {
        setAiSuggestionsModalOpen(false);
        onSubmit(null);
    }, [onSubmit]);

    return (
        <div className='vs-session-text-chat'>
            <div className='chat-content'>
                <TextChatMessageList
                    messages={messages}
                    participants={participants}
                    localParticipantId={localParticipantId}
                    userParticipantIds={userParticipantIds}
                    downloadingFiles={downloadingFiles}
                    downloadFile={downloadFile}
                    downloadFileDisabled={downloadFileDisabled}
                    messagesEndRef={messagesEndRef}
                    typingRemoteParticipantIds={typingRemoteParticipantIds}
                    getTranslation={getTranslation}
                />
            </div>
            {complianceFlagged &&
                <CompliancePanel
                    result={complianceFlagged.result}
                    originalContent={complianceFlagged.originalContent}
                    onAccept={onComplianceAccept}
                    onIgnore={onComplianceIgnore}
                    onDismiss={onComplianceDismiss}
                />}
            {onSubmit &&
                <form
                    className='form tw-flex tw-items-center'
                    onSubmit={onSubmit}
                >
                    <div className='form-input-wrapper'>
                        <input
                            className='form-input'
                            type='text'
                            value={message}
                            placeholder={t(T_KEYS.INPUT_PLACEHOLDER)}
                            maxLength={1000}
                            onChange={(event) => onChange(event.target.value)}
                        />
                        {voiceInput?.enabled &&
                            <Icon
                                name='microphone-on'
                                color={voiceInput.recording ? 'alert' : 'primary'}
                                button
                                className='tw-ml-2'
                                margin={false}
                                // Hold to talk: release (or drag the cursor away) always stops the recording,
                                // so a lost mouseup can't leave the microphone open.
                                onMouseDown={handleVoiceStart}
                                onMouseUp={handleVoiceStop}
                                onMouseLeave={handleVoiceStop}
                                onTouchStart={handleVoiceStart}
                                onTouchEnd={handleVoiceStop}
                                onTouchCancel={handleVoiceStop}
                            />}
                        {sendInProgress &&
                            <div className='tw-relative tw-w-6 tw-mr-1 tw-ml-2 tw-flex-center'><Loader /></div>}
                        {!sendInProgress &&
                            <Icon
                                name='send'
                                color='primary'
                                button
                                className='tw-mr-1 tw-ml-2'
                                margin={false}
                                disabled={submitDisabled}
                                onClick={onSubmit}
                            />}
                    </div>

                    {(aiSuggestionsEnabled || !uploadFileHidden) &&
                        <Dropdown
                            distance={40}
                            hideOnClick
                            triggerNode={() => (
                                <div className='tw-bg-layout tw-rounded-full tw-p-2 tw-ml-4 tw-cursor-pointer'>
                                    <Icon name='more-horizontal' size='lg' />
                                </div>
                            )}
                            childrenClassName='tw-rounded-xl tw-overflow-hidden tw--translate-y-10'
                        >
                            {aiSuggestionsEnabled &&
                                <DropdownItem
                                    disabled={aiSuggestionsDisabled}
                                    className='tw-flex tw-items-center'
                                    onClick={handleOpenChatbotHelp}
                                >
                                    <Icon name='chatbot' size='xl' button className='tw-mr-2 tw-w-8' />
                                    {t(T_KEYS.ACTIONS.CHATBOT_HELP)}
                                </DropdownItem>}

                            {!uploadFileHidden &&
                                <DropdownItem
                                    disabled={fileIsUploading}
                                    className='tw-flex tw-items-center'
                                    onClick={openFileFileInput}
                                >
                                    <div className='tw-flex tw-justify-center'>
                                        {fileIsUploading &&
                                            <div className='tw-relative tw-w-8 tw-h-full tw-mr-2 tw-flex-center'><Loader /></div>}
                                        {!fileIsUploading && <Icon
                                            name='paper-clip'
                                            button
                                            size='lg'
                                            className='tw-mr-2 tw-w-8'
                                        />}
                                        {t(T_KEYS.ACTIONS.ATTACH_FILE)}
                                    </div>
                                </DropdownItem>}
                        </Dropdown>}
                    {aiSuggestionsEnabled &&
                        <AiSuggestionsPopup
                            open={aiSuggestionsModalOpen}
                            suggestions={aiSuggestions}
                            loading={aiSuggestionsLoading}
                            error={aiSuggestionsError}
                            onChange={onChange}
                            onClose={handleCloseChatbotHelp}
                            onSubmit={handleSubmitSuggestion}
                        />}
                </form>}
            <FileInput
                setInputRef={setSendFileInputRef}
                onUpload={handleUploadFile}
            />
        </div>
    );
};
